import { useEffect, useState, useRef } from 'react';
import BottomSheet from '../../DesignSystem/BottomSheet';
import { useSelector } from 'react-redux';
import {
  editPatientV2,
  getPatients,
  getUserRelations,
  removePatient,
  saveNewPatientV2,
} from '../../../../../services';
import s from './PatientList.module.scss';
import { ReactComponent as EditIcon } from '../../Icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../Icons/Delete.svg';
import { ReactComponent as CrossIcon } from '../../Icons/Cross.svg';
import { ReactComponent as UserIcon } from '../../Icons/User.svg';
import { ReactComponent as ChevronDown } from '../../Icons/AngleDown.svg';
import { clsx } from '../../utils';
import { logEvent } from '../../../../../utils/logEvent';

/* ================ Helpers ================ */

const getDaysInMonth = (month, year) => {
  if (!month || !year) return 31;
  return new Date(year, month, 0).getDate();
};

const isValidYear = (year) => {
  if (year.length !== 4) return false;
  const y = Number(year);
  const currentYear = new Date().getFullYear();
  return y >= 1900 && y <= currentYear;
};

// pad 1–9 → 01–09
const padSingleDigit = (v) => {
  if (v.length === 1 && Number(v) >= 1 && Number(v) <= 9) {
    return `0${v}`;
  }
  return v;
};

/* ================= Component ================= */

const PatientList = ({
  isOpen,
  onClose,
  cartId,
  onPatientSelect,
  serviceLine,
}) => {
  const [userRelations, setUserRelations] = useState([]);
  const [patientList, setPatientList] = useState([]);
  const [showAddPatient, setShowAddPatient] = useState(false);

  const [relationSearch, setRelationSearch] = useState('');
  const [selectedRelation, setSelectedRelation] = useState(null);
  const [showRelationDropdown, setShowRelationDropdown] = useState(false);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobYear, setDobYear] = useState('');

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);

  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingRelations, setLoadingRelations] = useState(false);

  const authToken = useSelector((state) => state?.user?.authToken);

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const resetForm = () => {
    setRelationSearch('');
    setSelectedRelation(null);
    setShowRelationDropdown(false);
    setFullName('');
    setPhone('');
    setDobDay('');
    setDobMonth('');
    setDobYear('');
    setIsEditMode(false);
    setEditingPatient(null);
  };

  /* ================= Data ================= */

  useEffect(() => {
    if (isOpen) {
      logEvent(`${serviceLine} Choose Patient Screen`);
      setLoadingPatients(true);
      getPatients(authToken, null, cartId)
        .then((res) => setPatientList(res?.patients || []))
        .finally(() => setLoadingPatients(false));
    }
  }, [isOpen]);

  useEffect(() => {
    if (showAddPatient) {
      setLoadingRelations(true);
      getUserRelations(authToken)
        .then((res) => setUserRelations(res?.relations || []))
        .finally(() => setLoadingRelations(false));
    }
  }, [showAddPatient]);

  /* ================= DOB ================= */

  const maxDays = getDaysInMonth(dobMonth, dobYear);

  useEffect(() => {
    if (dobDay && Number(dobDay) > maxDays) {
      setDobDay('');
    }
  }, [dobMonth, dobYear]);

  /* ================= Validation ================= */

  const isFormValid =
    selectedRelation &&
    fullName.trim() &&
    phone.length === 10 &&
    dobDay.length === 2 &&
    dobMonth.length === 2 &&
    dobYear.length === 4 &&
    isValidYear(dobYear) &&
    Number(dobDay) <= maxDays;

  /* ================= API ================= */

  const refreshPatients = () =>
    getPatients(authToken, null, cartId).then((res) =>
      setPatientList(res?.patients || [])
    );

  const deletePatient = (id) =>
    removePatient(id, authToken).then(refreshPatients);

  const updatePatient = (id, payload) =>
    editPatientV2({ patientId: id, ...payload }, authToken).then(
      refreshPatients
    );

  const addNewPatient = (payload) =>
    saveNewPatientV2(payload, authToken).then((res) => {
      logEvent(serviceLine + ' Patient Added', {
        screenName: serviceLine + ' Add Patient Screen',
        category: serviceLine,
        patientId: res.patientId,
        patientName: payload?.name,
      });
      refreshPatients();
    });

  /* ================= Render ================= */

  return (
    <>
      {/* Patient List */}
      <BottomSheet isOpen={isOpen} onClose={onClose} noPadding>
        <div className={s.root}>
          <div className={s.header}>
            <p className={s.title}>Select Member</p>
            <button onClick={onClose}>
              <CrossIcon height={12} width={12} />
            </button>
          </div>

          <div className={s.content}>
            {loadingPatients ? (
              <div className={s.loaderWrapper}>
                <div className={s.spinner} />
              </div>
            ) : (
              patientList.map((patient) => (
                <div
                  key={patient.id}
                  className={s.patientCard}
                  onClick={() => {
                    logEvent(`${serviceLine} Patient Selected`, {
                      dependentId: patient?.patientId,
                    });
                    onPatientSelect(patient);
                  }}
                >
                  <img src={patient?.relation3dIcon} alt="" />
                  <div className={s.info}>
                    <p className={s.name}>{patient?.name}</p>
                    <p className={s.phoneGender}>
                      {patient?.gender}, {patient?.phone}
                    </p>
                  </div>

                  {!patient?.self && !patient?.relativeId && (
                    <div className={s.actions}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const [y, m, d] =
                            patient?.dateOfBirth?.split('-') || [];

                          setIsEditMode(true);
                          setEditingPatient(patient);
                          setFullName(patient.name || '');
                          setPhone(patient.phone || '');
                          setDobYear(y || '');
                          setDobMonth(m || '');
                          setDobDay(d || '');

                          setSelectedRelation({
                            relationId: patient?.relationId,
                            relationTitle: patient?.relationType,
                            relation3dIcon: patient?.relation3dIcon,
                            gender: patient?.gender,
                          });

                          setShowAddPatient(true);
                        }}
                      >
                        <EditIcon height={18} width={18} />
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deletePatient(patient?.patientId);
                        }}
                      >
                        <DeleteIcon height={18} width={18} />
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div
            className={s.addNewButton}
            onClick={() => {
              logEvent(`${serviceLine} Add Patient`);
              resetForm();
              setShowAddPatient(true);
            }}
          >
            <div className={s.addIcon}>+</div>
            Add New Patient
          </div>
        </div>
      </BottomSheet>

      {/* Add / Edit Patient */}
      <BottomSheet
        isOpen={showAddPatient}
        onClose={() => {
          setShowAddPatient(false);
          resetForm();
        }}
        noPadding
        height="70vh"
      >
        <div className={s.addNewRoot}>
          <div className={s.header}>
            <div className={s.title}>
              <UserIcon height={20} width={20} color="#CFC8FF" />
              <p className={s.text}>
                {isEditMode ? 'Edit Patient Details' : 'Add New Patient'}
              </p>
            </div>
            <button onClick={() => setShowAddPatient(false)}>
              <CrossIcon height={12} width={12} />
            </button>
          </div>

          <div className={s.content}>
            {/* Relationship */}
            {/* Relationship */}
            <div className={s.field}>
              <p className={s.label}>Relationship</p>

              <div
                className={s.relationInputWrapper}
                onClick={() => {
                  setShowRelationDropdown(true);
                  setRelationSearch('');
                }}
              >
                {selectedRelation?.relation3dIcon ? (
                  <img
                    src={selectedRelation?.relation3dIcon}
                    className={clsx(
                      s.relationThumb,
                      selectedRelation && s.border
                    )}
                    alt=""
                  />
                ) : (
                  <span className={s.relationThumb}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="11"
                        cy="11"
                        r="7"
                        stroke="#CFC8FF"
                        stroke-width="2"
                      />
                      <line
                        x1="16.65"
                        y1="16.65"
                        x2="21"
                        y2="21"
                        stroke="#CFC8FF"
                        stroke-width="2"
                        stroke-linecap="round"
                      />
                    </svg>
                  </span>
                )}
                <input
                  className={clsx(
                    s.relationInput,
                    selectedRelation && s.relationInputWithImage
                  )}
                  placeholder="Search Relationship"
                  value={
                    showRelationDropdown
                      ? relationSearch
                      : selectedRelation?.relationTitle || ''
                  }
                  onChange={(e) => {
                    setRelationSearch(e.target.value);
                    setSelectedRelation(null);
                    setShowRelationDropdown(true);
                  }}
                  onFocus={() => {
                    setShowRelationDropdown(true);
                    setRelationSearch('');
                    setSelectedRelation(null);
                  }}
                  readOnly={!!selectedRelation && !showRelationDropdown}
                />

                {/* Dropdown Icon */}
                <ChevronDown
                  className={clsx(
                    s.dropdownIcon,
                    showRelationDropdown && s.open
                  )}
                />
              </div>

              {showRelationDropdown && (
                <div className={s.dropdown}>
                  {loadingRelations ? (
                    <div className={s.loaderWrapper}>
                      <div className={s.spinner} />
                    </div>
                  ) : (
                    userRelations
                      .filter((r) =>
                        r.relationTitle
                          .toLowerCase()
                          .includes(relationSearch.toLowerCase())
                      )
                      .map((relation) => (
                        <div
                          key={relation.id}
                          className={s.option}
                          onClick={() => {
                            setSelectedRelation(relation);
                            setRelationSearch('');
                            setShowRelationDropdown(false);
                          }}
                        >
                          <img src={relation.relation3dIcon} alt="" />
                          <p>{relation.relationTitle}</p>
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>

            {/* DOB */}
            <div className={s.field}>
              <p className={s.label}>Date of Birth</p>
              <div className={s.dobRow}>
                <input
                  ref={dayRef}
                  className={s.dobInput}
                  placeholder="DD"
                  value={dobDay}
                  maxLength={2}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    if (v === '' || Number(v) <= maxDays) {
                      setDobDay(v);
                      if (v.length === 2) monthRef.current.focus();
                    }
                  }}
                  onBlur={() => setDobDay(padSingleDigit)}
                />

                <input
                  ref={monthRef}
                  className={s.dobInput}
                  placeholder="MM"
                  value={dobMonth}
                  maxLength={2}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    if (v === '' || Number(v) <= 12) {
                      setDobMonth(v);
                      if (v.length === 2) yearRef.current.focus();
                    }
                  }}
                  onBlur={() => setDobMonth(padSingleDigit)}
                />

                <input
                  ref={yearRef}
                  className={s.dobInput}
                  placeholder="YYYY"
                  value={dobYear}
                  maxLength={4}
                  onChange={(e) =>
                    setDobYear(e.target.value.replace(/\D/g, ''))
                  }
                  onBlur={() => {
                    if (!dobYear) return; // optional: ignore empty
                    let yearNum = Number(dobYear);

                    if (yearNum < 1900) yearNum = 1900;
                    if (yearNum > 2100) yearNum = 2100;

                    setDobYear(String(yearNum));
                  }}
                />
              </div>
            </div>

            {/* Name */}
            <div className={s.field}>
              <p className={s.label}>Full Name</p>
              <input
                className={s.normalInput}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter Full Name"
                maxLength={25}
              />
            </div>

            {/* Phone */}
            <div className={s.field}>
              <p className={s.label}>Phone Number</p>
              <input
                className={s.normalInput}
                value={phone}
                maxLength={10}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter Phone Number"
              />
            </div>
          </div>

          <button
            className={s.submitBtn}
            disabled={!isFormValid}
            onClick={() => {
              const payload = {
                name: fullName.trim(),
                phone,
                dob: `${dobYear}-${dobMonth}-${dobDay}`,
                relationId: selectedRelation?.relationId,
                gender: selectedRelation?.gender,
              };

              isEditMode
                ? updatePatient(editingPatient?.patientId, payload)
                : addNewPatient(payload);

              setShowAddPatient(false);
              resetForm();
            }}
          >
            Confirm
          </button>
        </div>
      </BottomSheet>
    </>
  );
};

export default PatientList;
